import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Highlighter } from "lucide-react";

const COLORS = [
  { value: "#C4B5FD", bg: "bg-[#C4B5FD]" },
  { value: "#FDE047", bg: "bg-[#FDE047]" },
  { value: "#86EFAC", bg: "bg-[#86EFAC]" },
  { value: "#7DD3FC", bg: "bg-[#7DD3FC]" },
];

export default function FloatingHighlight({ api, paperId, currentPage, onHighlightSaved }) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState("");

  const handleMouseUp = useCallback(() => {
    // Small delay to let selection finalize
    setTimeout(() => {
      const selection = window.getSelection();
      const text = selection?.toString().trim();
      if (text && text.length >= 3) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setPosition({
          x: rect.left + rect.width / 2,
          y: rect.top - 10,
        });
        setSelectedText(text);
        setShow(true);
      } else {
        setShow(false);
        setSelectedText("");
      }
    }, 50);
  }, []);

  const handleMouseDown = useCallback(() => {
    setShow(false);
  }, []);

  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [handleMouseUp, handleMouseDown]);

  const saveHighlight = async (color) => {
    if (!selectedText) return;
    try {
      const res = await axios.post(`${api}/highlights/json`, {
        paper_id: paperId,
        page_number: currentPage,
        text: selectedText,
        color: color,
      }, { withCredentials: true });
      if (onHighlightSaved) onHighlightSaved(res.data);
      toast.success("Highlighted!");
      window.getSelection()?.removeAllRanges();
      setShow(false);
      setSelectedText("");
    } catch {
      toast.error("Failed to highlight");
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed z-[100] -translate-x-1/2 -translate-y-full"
      style={{ left: position.x, top: position.y }}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid="floating-highlight"
    >
      <div className="bg-white dark:bg-[#1E1E1E] border-2 border-neo-border dark:border-[#444] shadow-[3px_3px_0px_0px_#1A1A1A] dark:shadow-[3px_3px_0px_0px_#444] rounded-lg px-2 py-1.5 flex items-center gap-1.5">
        <Highlighter className="w-3.5 h-3.5 text-neo-text-secondary dark:text-gray-400" />
        {COLORS.map((c) => (
          <button
            key={c.value}
            onClick={(e) => { e.stopPropagation(); saveHighlight(c.value); }}
            className={`w-6 h-6 rounded-full border-2 border-neo-border/50 dark:border-[#555] hover:scale-125 transition-transform ${c.bg}`}
            data-testid={`float-highlight-${c.value}`}
          />
        ))}
      </div>
      {/* Arrow */}
      <div className="flex justify-center">
        <div className="w-3 h-3 bg-white dark:bg-[#1E1E1E] border-r-2 border-b-2 border-neo-border dark:border-[#444] transform rotate-45 -mt-1.5" />
      </div>
    </div>
  );
}
